import { CaretRightOutlined } from '@ant-design/icons';
import { Alert, Button, Collapse, Space, Spin, Table, Tag, theme } from 'antd';
import { format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import MpOfferDetailDrawer from '../../../mpOffers/detail/MpOfferDetailDrawer';
import { useGetMpOffers } from '../../../../../utils/api/mpOffers/useGetMpOffers';
import {
  OFFER_PUBLICATION_STATE,
  OFFER_PUBLISH_STATUS,
} from '../../../../../utils/const/mpOffers';

const DASH = '—';

const formatPrice = (value, currency) => {
  if (value == null) return DASH;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return DASH;
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return DASH;
  }
};

const statusTag = (statuses, value) => {
  if (!value) return DASH;
  const { color, label } = statuses[value] || {
    color: 'default',
    label: value,
  };
  return <Tag color={color}>{label}</Tag>;
};

const columns = [
  {
    title: 'Marketplace',
    key: 'marketplace',
    render: (_, offer) => (
      <Space size={8}>
        <span style={{ color: '#8c8c8c', textTransform: 'capitalize' }}>
          {offer.store?.marketplace?.name || DASH}
        </span>
        {offer.store?.country && <Tag>{offer.store.country}</Tag>}
      </Space>
    ),
  },
  {
    title: 'Statut',
    dataIndex: 'publication_state',
    key: 'publication_state',
    render: (state) => statusTag(OFFER_PUBLICATION_STATE, state),
  },
  {
    title: 'Statut publish',
    dataIndex: 'publish_status',
    key: 'publish_status',
    render: (status) => statusTag(OFFER_PUBLISH_STATUS, status),
  },
  {
    title: 'Prix',
    key: 'price',
    render: (_, offer) => formatPrice(offer.price, offer.currency),
  },
  {
    title: 'Stock',
    dataIndex: 'quantity',
    key: 'quantity',
    render: (quantity) => (
      <Space size={8}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor:
              quantity === 0
                ? '#ff4d4f'
                : quantity > 50
                  ? '#52c41a'
                  : '#fa8c16',
          }}
        />
        <span>{quantity ?? DASH}</span>
      </Space>
    ),
  },
  {
    title: 'Dernier envoi API',
    dataIndex: 'last_pushed_at',
    key: 'last_pushed_at',
    render: formatDate,
  },
];

const Offers = ({ productDetails }) => {
  const { token } = theme.useToken();
  const [detailOfferId, setDetailOfferId] = useState(null);

  const { mpOffers, count, isLoading, isError, refetch } = useGetMpOffers({
    product: productDetails.id,
    limit: 200,
  });

  const groups = useMemo(() => {
    const byMarketplace = new Map();

    (mpOffers ?? []).forEach((offer) => {
      const marketplace = offer.store?.marketplace;
      const key = marketplace?.id ?? 'unknown';

      if (!byMarketplace.has(key)) {
        byMarketplace.set(key, {
          key,
          name: marketplace?.name || 'Sans marketplace',
          offers: [],
        });
      }
      byMarketplace.get(key).offers.push(offer);
    });

    return Array.from(byMarketplace.values());
  }, [mpOffers]);

  const panelStyle = {
    marginBottom: 24,
    background: token.colorBgContainer,
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
  };

  const items = groups.map((group) => {
    const diffused = group.offers.filter(
      (offer) => offer.publication_state === 'up_to_date'
    ).length;
    const pending = group.offers.filter(
      (offer) => offer.publication_state === 'pending'
    ).length;

    return {
      key: group.key,
      style: panelStyle,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <span
            style={{
              fontWeight: 500,
              color: '#8c8c8c',
              textTransform: 'capitalize',
            }}
          >
            {group.name}
          </span>
          <Space size={8}>
            {diffused > 0 && <Tag color="green">{diffused} diffusés</Tag>}
            {pending > 0 && (
              <Tag color="gold">{pending} diffusion en cours</Tag>
            )}
          </Space>
        </div>
      ),
      children: (
        <Table
          columns={columns}
          dataSource={group.offers}
          rowKey="id"
          size="small"
          pagination={false}
          onRow={(offer) => ({
            onClick: () => setDetailOfferId(offer.id),
            style: { cursor: 'pointer' },
          })}
        />
      ),
    };
  });

  return (
    <div style={{ padding: '0 24px 24px 24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0 }}>Les offres ({count ?? 0})</h1>
        <Link to="/dashboard/mp-offers">Voir le tableau des offres →</Link>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin />
        </div>
      ) : isError ? (
        <Alert
          type="error"
          showIcon
          message="Impossible de charger les offres."
          action={
            <Button size="small" onClick={() => refetch()}>
              Réessayer
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Aucune offre marketplace pour ce produit.
        </div>
      ) : (
        <Collapse
          bordered={false}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ background: token.colorBgContainer }}
          items={items}
        />
      )}

      <MpOfferDetailDrawer
        offerId={detailOfferId}
        open={detailOfferId !== null}
        onClose={() => setDetailOfferId(null)}
      />
    </div>
  );
};

export default Offers;
